import { useState } from "react";
import PropTypes from "prop-types";
import {greyishBlue50, greyishBlue100} from "assets/jss/main";

const fakeRows = [
    {
        id: 1,
        sku: "SKU-12345",
        emoji: "👕",
        categorie: "Haut",
        price: 29.99,
        previousPrice: 24.99,
        stock: 456,
        taille: "M/L/XL",
        statut: "Enrichi",
        completude: 92,
        tag: "IA",
        tagColor: "#4338ca",
        tagBg: "#eef2ff",
    },
    {
        id: 2,
        sku: "SKU-12346",
        emoji: "👖",
        categorie: "Bas",
        price: 79.99,
        previousPrice: 59.99,
        stock: 234,
        taille: "28-36",
        statut: "Enrichi",
        completude: 88,
        tag: "Max",
        tagColor: "#16a34a",
        tagBg: "#f0fdf4",
    },
    {
        id: 3,
        sku: "SKU-12348",
        emoji: "🧥",
        categorie: "Veste",
        price: 299.99,
        previousPrice: 249.99,
        stock: 23,
        taille: "S/M/L",
        statut: "Enrichi",
        completude: 95,
        tag: "IA + Sabine",
        tagColor: "#4338ca",
        tagBg: "#eef2ff",
    },
];

// ─── helpers ──────────────────────────────────────────────────────────────────
const getProgressColor = (pct) => {
    if (pct < 50) return "#ef4444";
    if (pct < 75) return "#f59e0b";
    return "#22c55e";
};

const statusConfig = {
    Enrichi:      { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
    "À enrichir": { color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
    Manquant:     { color: "#ef4444", bg: "#fff0f0", border: "#fecaca" },
};

// ─── cell styles ──────────────────────────────────────────────────────────────
const cellStyle = {
    padding: "16px 12px",
    borderBottom: `1px solid ${greyishBlue50}`,
    fontSize: "14px",
    color: "#374151",
    verticalAlign: "middle",
};

const headerCellStyle = {
    padding: "12px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#9ca3af",
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${greyishBlue50}`,
    backgroundColor: "#ffffff",
    whiteSpace: "nowrap",
};

// TODO: redo all this component after demo
function FakeEnrichedTable({ rows, history, match, baseParamsUrl }) {
    const [selected, setSelected] = useState([]);
    const [hovered, setHovered] = useState(null);

    const allChecked = selected.length === rows.length;

    const toggleAll = () =>
        setSelected(allChecked ? [] : rows.map((r) => r.id));

    const toggleOne = (id) =>
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );

    const handleEnrichir = (e, row) => {
        e.stopPropagation();
        const product = {
            id: row.id,
            sku: row.sku,
            outletPrice: row.price,
            lowestPrice: row.previousPrice || row.price,
            highestPrice: row.price,
            status: row.statut === "Enrichi",
            pictures: [], // No pictures in fake data, PimShow will use default
            brand: { name: row.tag }, // Using tag as brand for demo
            translations: { fr_FR: {locale: 'fr_FR', name: `Product ${row.sku}`,  description: "Ceci est une description"  }}, // Minimal translation
            variations: [
                {
                    stock: row.stock,
                    translations: { fr_FR: {locale: 'fr_FR', name: `Product ${row.sku}`}}
                }
            ]
        };

        history.push({
            pathname: `${baseParamsUrl + match.params.id}/products/pim/${row.id}`,
            state: { enrichmentProduct: product }
        });
    };

    return (
        <div style={{
            fontFamily: "'Segoe UI', sans-serif",
            border: `1px solid ${greyishBlue100}`,
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#ffffff",
        }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>

                {/* ── HEADER ── */}
                <thead>
                <tr>
                    <th style={{ ...headerCellStyle, width: "40px", padding: "12px 8px 12px 16px" }}>
                        <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={toggleAll}
                            style={{ accentColor: "#4338ca", width: "15px", height: "15px", cursor: "pointer" }}
                        />
                    </th>
                    {["SKU", "Image", "Catégorie", "Prix", "Stock", "Taille", "Statut", "Complétude", "Tag", "", "Actions"].map((col, i) => (
                        <th key={i} style={headerCellStyle}>{col}</th>
                    ))}
                </tr>
                </thead>

                {/* ── BODY ── */}
                <tbody>
                {rows.map((row) => {
                    const sc = statusConfig[row.statut] || { color: "#6b7280", bg: greyishBlue50, border: greyishBlue100 };
                    const isHovered = hovered === row.id;

                    return (
                        <tr
                            key={row.id}
                            onMouseEnter={() => setHovered(row.id)}
                            onMouseLeave={() => setHovered(null)}
                            style={{ backgroundColor: isHovered ? "#fafafa" : "#ffffff", transition: "background-color 0.15s" }}
                        >
                            {/* Checkbox */}
                            <td style={{ ...cellStyle, padding: "16px 8px 16px 16px" }}>
                                <input
                                    type="checkbox"
                                    checked={selected.includes(row.id)}
                                    onChange={() => toggleOne(row.id)}
                                    style={{ accentColor: "#4338ca", width: "15px", height: "15px", cursor: "pointer" }}
                                />
                            </td>

                            {/* SKU */}
                            <td style={{ ...cellStyle, fontWeight: "600", color: "#111827", whiteSpace: "nowrap" }}>
                                {row.sku}
                            </td>

                            {/* Image (emoji) */}
                            <td style={{ ...cellStyle, fontSize: "28px", lineHeight: 1 }}>
                                {row.emoji}
                            </td>

                            {/* Catégorie */}
                            <td style={{ ...cellStyle, color: "#6b7280" }}>
                                {row.categorie}
                            </td>

                            {/* Prix */}
                            <td style={cellStyle}>
                                <div style={{ fontWeight: "700", color: "#111827" }}>{row.price}€</div>
                                {row.previousPrice && (
                                    <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>
                                        bas 30j : {row.previousPrice}€
                                    </div>
                                )}
                            </td>

                            {/* Stock */}
                            <td style={cellStyle}>{row.stock}</td>

                            {/* Taille */}
                            <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>{row.taille}</td>

                            {/* Statut */}
                            <td style={cellStyle}>
                  <span style={{
                      display: "inline-block",
                      padding: "4px 12px",
                      borderRadius: "999px",
                      border: `1px solid ${sc.border}`,
                      backgroundColor: sc.bg,
                      color: sc.color,
                      fontSize: "12px",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                  }}>
                    {row.statut}
                  </span>
                            </td>

                            {/* Complétude */}
                            <td style={cellStyle}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <div style={{
                                        width: "80px", height: "6px",
                                        borderRadius: "999px", backgroundColor: greyishBlue100,
                                        overflow: "hidden", flexShrink: 0,
                                    }}>
                                        <div style={{
                                            width: `${row.completude}%`,
                                            height: "100%",
                                            backgroundColor: getProgressColor(row.completude),
                                            borderRadius: "999px",
                                        }} />
                                    </div>
                                    <span style={{ fontSize: "13px", fontWeight: "500", whiteSpace: "nowrap" }}>
                      {row.completude}%
                    </span>
                                </div>
                            </td>

                            {/* Tag */}
                            <td style={cellStyle}>
                  <span style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: "999px",
                      backgroundColor: row.tagBg,
                      color: row.tagColor,
                      fontSize: "12px",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                  }}>
                    {row.tag}
                  </span>
                            </td>

                            {/* Spacer */}
                            <td style={cellStyle} />

                            {/* Actions */}
                            <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <button style={{
                                        display: "inline-flex", alignItems: "center", gap: "6px",
                                        background: "none", border: "none", cursor: "pointer",
                                        fontSize: "13px", color: "#6b7280", fontFamily: "inherit", padding: 0,
                                    }}
                                            onMouseEnter={e => e.currentTarget.style.color = "#111827"}
                                            onMouseLeave={e => e.currentTarget.style.color = "#6b7280"}
                                            onClick={(e) => handleEnrichir(e, row)}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="2" x2="22" y2="6" /><path d="M7.5 20.5L19 9l-4-4L3.5 16.5 2 22z" />
                                        </svg>
                                        Enrichir
                                    </button>
                                    <button style={{
                                        background: "none", border: "none", cursor: "pointer",
                                        color: "#9ca3af", display: "flex", alignItems: "center", padding: 0,
                                    }}
                                            onMouseEnter={e => e.currentTarget.style.color = "#374151"}
                                            onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
}

FakeEnrichedTable.propTypes = {
    history: PropTypes.object,
    match: PropTypes.object,
    baseParamsUrl: PropTypes.string,
    rows: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            sku: PropTypes.string.isRequired,
            emoji: PropTypes.string.isRequired,
            categorie: PropTypes.string.isRequired,
            price: PropTypes.number.isRequired,
            previousPrice: PropTypes.number,
            stock: PropTypes.number.isRequired,
            taille: PropTypes.string.isRequired,
            statut: PropTypes.string.isRequired,
            completude: PropTypes.number.isRequired,
            tag: PropTypes.string,
            tagColor: PropTypes.string,
            tagBg: PropTypes.string,
        })
    ),
};

FakeEnrichedTable.defaultProps = {
    rows: fakeRows,
};

export default FakeEnrichedTable;
